import type { ListBuilderList } from '../../../types';
import {
	createClientActionsColumn,
	createClientNameColumn,
	createClientRoutesColumn,
	createContactInfoColumn,
	createLocationColumn,
	createSalespeopleColumn,
} from '../columns';
import {
	CLIENT_MOBILE_CARD_CONFIG,
	DEFAULT_LIST_REQUIRED_FIELDS,
	SALESPEOPLE_FILTER_RULES,
} from '../shared';

/**
 * PD-15504 — Routing list for clients.
 *
 * Answers "is this customer on a route?" for a whole book at once, and — with
 * the Routed filter — "which ones are not?". Without it, that question meant
 * opening routes one at a time to see who was on them.
 *
 * Named to match the orders Routing list and leads with the same route
 * membership column, so the two read as the same idea seen from either end:
 * that list asks whether an ORDER is on a trip, this one asks whether a
 * CUSTOMER is covered by a route.
 *
 * Bundled for every tenant but INACTIVE by default — it joins a user's tab
 * strip only when they enable it, exactly like the orders Routing list, so
 * tenants that do not use routing never see it.
 */
export const clientRoutingList: ListBuilderList = {
	id: 'client-routing',
	cacheTTL: 600000,
	name: 'Routing',
	type: 'default',
	active: false,
	bulkActionTemplate: 'clients',
	sortOrder: 15,
	columns: [
		createClientNameColumn(),
		createClientRoutesColumn(),
		createLocationColumn(),
		createSalespeopleColumn(),
		createContactInfoColumn(),
		createClientActionsColumn(),
	],
	mobileCardConfig: CLIENT_MOBILE_CARD_CONFIG,
	description: 'Customers and the routes that cover them',
	defaultSortId: 'column-client-name-asc',
	// `routes` is what the Routes column reads; ClientsService only does the
	// membership lookup when it is asked for. `client.lastStopDate` (PD-15698)
	// opts in the last-completed-stop join for the column's second line.
	requiredFields: [
		...DEFAULT_LIST_REQUIRED_FIELDS,
		'routes',
		'client.lastStopDate',
	],
	// PD-15698 — this list exists to answer "who is covered, and who haven't
	// we visited?", so Days Since Last Stop sits right after Search (the
	// inline bar takes filters in this order), with Routed next. The rest
	// mirror the shared client-list order.
	activeFilters: [
		'clientSearch',
		'lastStopDays',
		'clientRouted',
		'clientCompany',
		'salespeople',
		'alternateRep',
		'lifecycleStageName',
		'businessTypes',
		'leadOrCustomer',
		'salesStatus',
		'callStatus',
		'clientState',
		'revenue90Days',
		'orderSalesStatus',
		'groups',
		'clientLists',
		'excludeClientLists',
		'clientDeletedRecords',
		'clientArchivedRecords',
	],
	filterRules: {
		// PD-15546 — the server derives a user's client visibility from the
		// requested list's salespeople rule. Without it this list was the one
		// client list that bypassed the `clients:list:any` bound, so a
		// `view:self`-only user could read the whole book via ?listId=client-routing.
		...SALESPEOPLE_FILTER_RULES,
		leadOrCustomer: {
			defaultValue: [{ id: 'Customer', label: 'Customer' }],
		},
	},
};
